<template>
  <span class="sst" :class="{ 'sst-busy': busy, 'sst-reduced': reduced }"
        role="status" aria-live="polite" data-testid="session-status">
    <!-- The session status over the chat (Chris, 2026-10-30): where the
         seeker is, in one short line (utils/session-status names it). A
         change cross-fades; while something is being made the line shimmers.
         Reduced motion: an instant swap, no shimmer. -->
    <!-- type: the fade times the swap, never the shimmer's endless loop. -->
    <Transition name="sst-fade" type="transition" :css="!reduced">
      <span :key="text" class="sst-line">{{ text }}</span>
    </Transition>
  </span>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { prefersReducedMotion } from '@/utils/lesson-presence'

defineProps({
  text: { type: String, default: '' },
  busy: { type: Boolean, default: false },
})

const reduced = ref(prefersReducedMotion())
let mq = null
const onChange = () => { reduced.value = prefersReducedMotion() }
onMounted(() => {
  try {
    mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)')
    if (mq && mq.addEventListener) mq.addEventListener('change', onChange)
  } catch (e) { mq = null }
})
onBeforeUnmount(() => {
  try { if (mq && mq.removeEventListener) mq.removeEventListener('change', onChange) } catch (e) { /* */ }
})
</script>

<style scoped>
/* Both lines share one cell while they cross: no jump, no reflow. */
.sst { display: inline-grid; justify-items: center; align-items: center; max-width: 100%; }
.sst-line { grid-area: 1 / 1; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.sst-fade-enter-active,
.sst-fade-leave-active { transition: opacity 250ms ease; }
.sst-fade-enter-from,
.sst-fade-leave-to { opacity: 0; }
/* Something is being made: a slow gold light passes along the words. */
.sst-busy .sst-line {
  background-image: linear-gradient(90deg,
    var(--cr-lavender, #b9a6e6) 0%, var(--cr-lavender, #b9a6e6) 38%,
    #ffe3ad 50%,
    var(--cr-lavender, #b9a6e6) 62%, var(--cr-lavender, #b9a6e6) 100%);
  background-size: 250% 100%;
  background-position: 100% 0;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  animation: sst-shimmer 2.4s ease-in-out infinite;
}
@keyframes sst-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0% 0; }
}
.sst-reduced.sst-busy .sst-line { animation: none; background-image: none; -webkit-text-fill-color: currentColor; color: inherit; }
@media (prefers-reduced-motion: reduce) {
  .sst-busy .sst-line { animation: none; background-image: none; -webkit-text-fill-color: currentColor; color: inherit; }
  .sst-fade-enter-active, .sst-fade-leave-active { transition: none; }
}
</style>
